/**
 * @module ui.location
 * @version 4.0.0
 * @description LIFTR Location Web Component — Singleton orchestrator for
 *              postal code input and browser geolocation.
 *
 * Owns the "Location Panel" (popup/sheet) with two input paths:
 *   1. Geolocation button → getUserPosition → BigDataCloud API → PLZ + City
 *   2. PLZ text input → Zippopotam.us API → lat/lng + City
 *
 * Emits `liftr:location:resolved` for all consumers (e.g. liftr-store).
 * Manages geo states on itself (loading / success / denied).
 * Persists resolved location to localStorage for cross-session restore.
 *
 * Composition: liftr-location wraps liftr-panel (Nested Pattern).
 *   - liftr-location = Parent (Orchestrator)
 *   - liftr-panel    = Child (Public API: open/close/toggle)
 *
 * @element liftr-location
 *
 * @fires liftr:location:resolved - Location resolved or cleared
 *
 * @attr {string} liftr-state - State output:
 *   loading:  Geolocation API in progress
 *   success:  Position obtained + PLZ resolved
 *   denied:   Permission denied or API unavailable
 *
 * CHANGELOG:
 * - 4.0.0: BREAKING - Single geo button with state-driven labels.
 *          Removed: 3 state divs (loading/success/denied) from DOM
 *          Removed: #geoToggleState, #setGeoToggleFx() (no child state)
 *          Added: ROLES.LABEL_ACTION, ROLES.LABEL_INFO for button text
 *          Added: #updateGeoButton(state) — sets labels + disabled per state
 *          Changed: Icon swap via CSS ::before mask (no more SVG sprites)
 *          Renamed: GLOBAL_ROLES.LABEL_ISO → LABEL_COUNTRY
 * - 3.0.0: Generic state values (loading/success/denied/error).
 * - 2.0.0: External APIs (Zippopotam.us, BigDataCloud).
 * - 1.0.0: Initial implementation
 */

import { LiftrModule } from './core.module.js';
import { ATTRIBUTES, i18n, on, emit, settings } from './core.liftr.js';
import { debounce, roleAllGlobal } from './core.utilities.js';
import * as geo from './api.geo.js';

const ROLES = Object.freeze({
  GEO_TOGGLE:    'location-geo-toggle',
  LABEL_ACTION:  'location-label-action',
  LABEL_INFO:    'location-label-info',
  POSTAL_INPUT:  'location-postal-input',
  POSTAL_DELETE: 'location-postal-delete'
});

const GLOBAL_ROLES = Object.freeze({
  LABEL_POSTAL:  'location-label-postal',
  LABEL_CITY:    'location-label-city',
  LABEL_COUNTRY: 'location-label-country'
});

const STATES = Object.freeze({
  LOADING: 'loading',
  SUCCESS: 'success',
  DENIED:  'denied',
  ERROR:   'error'
});

const EVENTS = Object.freeze({
  RESOLVED: 'resolved'
});

const SETTINGS = Object.freeze({});

/**
 * External events this module listens to.
 * NOT exported (internal wiring only).
 */
const LISTEN = Object.freeze({
  PANEL_CLOSED: 'liftr:panel:closed'
});

const GEO_STATE_TOKENS = [
  STATES.LOADING, STATES.SUCCESS, STATES.DENIED, STATES.ERROR
];

/* ═══════════════════════════════════════════════════════════════════
 * LOCATION STORAGE
 * ═══════════════════════════════════════════════════════════════════ */

const STORAGE_KEY = 'liftr:postal-code';
const STORAGE_MAX_AGE_DAYS = 30;

/**
 * @param {string} code
 * @param {string} city
 * @param {number} lat
 * @param {number} lng
 */
function saveLocation(code, city, lat, lng) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      code, city, lat, lng,
      timestamp: Date.now()
    }));
  } catch { /* silent */ }
}

/**
 * @returns {{ code: string, city: string|null, lat: number|null, lng: number|null } | null}
 */
function loadLocation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const ageDays = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
    if (ageDays > STORAGE_MAX_AGE_DAYS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return {
      code: data.code || null,
      city: data.city ?? null,
      lat: data.lat ?? null,
      lng: data.lng ?? null
    };
  } catch {
    return null;
  }
}

function deleteLocation() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* silent */ }
}

class LiftrLocation extends LiftrModule {

  static NAME = 'location';

  static DEFAULTS = {
    events: ['input', 'blur'],
    pattern: null
  };

  #panelId = null;
  #elGeoToggle = null;
  #elLabelAction = null;
  #elLabelInfo = null;
  #elPostalInput = null;
  #elPostalDelete = null;
  #elsPostalDisplay = [];
  #elsCountryDisplay = [];
  #elsCityDisplay = [];
  #postalDisplayDefault = '';
  #cityDisplayDefault = '';
  #resolvedCode = null;
  #resolvedCity = null;
  #resolvedSource = null;
  #patternRe = null;
  #declaredEvents = ['input', 'blur'];
  #resolveVersion = 0;

  mount() {
    this.#queryElements();
    this.#setupConfig();
    this.#setupGeo();
    this.#setupPostal();
    this.#setupPanelClose();
    this.#setupPermissionWatch();
    this.#restoreFromStorage();
  }

  unmount() {
    this.state.remove(...GEO_STATE_TOKENS);
    this.#resolvedCode = null;
    this.#resolvedCity = null;
    this.#resolvedSource = null;
    super.unmount();
  }

  #queryElements() {
    this.#elGeoToggle    = this.role(ROLES.GEO_TOGGLE);
    this.#elLabelAction  = this.role(ROLES.LABEL_ACTION);
    this.#elLabelInfo    = this.role(ROLES.LABEL_INFO);
    this.#elPostalInput  = this.role(ROLES.POSTAL_INPUT);
    this.#elPostalDelete = this.role(ROLES.POSTAL_DELETE);

    this.#elsPostalDisplay = roleAllGlobal(GLOBAL_ROLES.LABEL_POSTAL);
    if (this.#elsPostalDisplay.length > 0) {
      this.#postalDisplayDefault = this.#elsPostalDisplay[0].textContent?.trim() || '';
    }

    this.#elsCityDisplay = roleAllGlobal(GLOBAL_ROLES.LABEL_CITY);
    if (this.#elsCityDisplay.length > 0) {
      this.#cityDisplayDefault = this.#elsCityDisplay[0].textContent?.trim() || '';
    }

    this.#elsCountryDisplay = roleAllGlobal(GLOBAL_ROLES.LABEL_COUNTRY);

    // Read child panel ID for close event filtering
    const panel = this.querySelector('liftr-panel');
    this.#panelId = panel?.getAttribute(ATTRIBUTES.ID) ?? null;
  }

  #setupConfig() {
    const cfg = this.config;
    if (typeof cfg.pattern === 'string' && cfg.pattern) {
      try {
        this.#patternRe = new RegExp(cfg.pattern);
      } catch { /* silent */ }
    }
    this.#declaredEvents = Array.isArray(cfg.events) && cfg.events.length
      ? cfg.events.slice()
      : ['input', 'blur'];
  }

  /* ─────────────────────────────────────────────────────────────────────
   * GEOLOCATION
   * ───────────────────────────────────────────────────────────────────── */

  #setupGeo() {
    if (!this.#elGeoToggle) return;
    const handler = () => this.#handleGeoClick();
    this.#elGeoToggle.addEventListener('click', handler);
    this.addDisposable(() => this.#elGeoToggle.removeEventListener('click', handler));
  }

  async #handleGeoClick() {
    if (this.state.has(STATES.LOADING) || this.state.has(STATES.SUCCESS)) return;

    this.#setGeoState(STATES.LOADING);
    this.#updateGeoButton(STATES.LOADING);
    const startTime = performance.now();

    // Step 1: Get browser position
    let position;
    try {
      position = await geo.getUserPosition({
        highAccuracy: true,
        timeout: 10000,
        maxAge: 60000
      });
    } catch {
      await this.awaitLoading(startTime);
      this.#setGeoState(STATES.DENIED);
      this.#updateGeoButton(STATES.DENIED);
      this.#notify('error', 'notification_geo_denied');
      return;
    }

    // Step 2: Reverse geocode via BigDataCloud
    const result = await geo.reverseGeocodeAPI(position);
    await this.awaitLoading(startTime);

    if (!result?.code) {
      this.#setGeoState(STATES.DENIED);
      this.#updateGeoButton(STATES.DENIED);
      this.#notify('error', 'notification_geo_denied');
      return;
    }

    // Step 3: Success — update everything
    const code = result.code;
    const city = result.city || '';
    const state = result.state || '';
    const countryCode = result.countryCode || 'DE';

    this.#setGeoState(STATES.SUCCESS);
    this.#updateGeoButton(STATES.SUCCESS, { city, state, countryCode });

    this.#updateCountryDisplay(countryCode);

    if (this.#elPostalInput) {
      this.#elPostalInput.value = code;
    }

    saveLocation(code, city, position.lat, position.lng);

    this.#resolvedCode = code;
    this.#resolvedCity = city;
    this.#resolvedSource = 'geolocation';
    this.#updatePostalDisplay(code);
    this.#updateCityDisplay(city);

    this.emit(EVENTS.RESOLVED, {
      valid: true,
      lat: position.lat,
      lng: position.lng,
      code,
      city,
      source: 'geolocation'
    });

    this.#notify('success', 'notification_geo_success', code);
  }

  /* ─────────────────────────────────────────────────────────────────────
   * GEO BUTTON — State-Driven Labels
   * ───────────────────────────────────────────────────────────────────── */

  /**
   * Update geo button labels and disabled state.
   * Icon swap is handled by CSS via liftr-state on root.
   *
   * @param {string|null} geoState - STATES.LOADING / SUCCESS / DENIED / null (idle)
   * @param {Object} [geo] - Geo data for success state
   * @param {string} [geo.city] - City name
   * @param {string} [geo.state] - Federal state / region
   * @param {string} [geo.countryCode] - ISO country code (e.g. 'DE')
   */
  #updateGeoButton(geoState, geo) {
    const labels = i18n.get('location') || {};

    switch (geoState) {
      case STATES.LOADING:
        this.#elLabelAction?.replaceChildren(
          document.createTextNode(labels.filter_geo_loading || `[label missing: location.filter_geo_loading]`)
        );
        this.#elLabelInfo?.replaceChildren();
        if (this.#elGeoToggle) this.#elGeoToggle.disabled = true;
        break;

      case STATES.SUCCESS: {
        this.#elLabelAction?.replaceChildren(
          document.createTextNode(labels.filter_geo_success || `[label missing: location.filter_geo_success]`)
        );
        const parts = [geo?.city, geo?.state].filter(Boolean).join(', ');
        const infoText = parts && geo?.countryCode
          ? `${parts} (${geo.countryCode})`
          : parts || geo?.countryCode || '';
        this.#elLabelInfo?.replaceChildren(
          document.createTextNode(infoText)
        );
        if (this.#elGeoToggle) this.#elGeoToggle.disabled = true;
        break;
      }

      case STATES.DENIED:
        this.#elLabelAction?.replaceChildren(
          document.createTextNode(labels.filter_geo_denied || `[label missing: location.filter_geo_denied]`)
        );
        this.#elLabelInfo?.replaceChildren(
          document.createTextNode(labels.filter_geo_denied_hint || `[label missing: location.filter_geo_denied_hint]`)
        );
        if (this.#elGeoToggle) this.#elGeoToggle.disabled = true;
        break;

      default:
        // Idle — restore defaults
        this.#elLabelAction?.replaceChildren(
          document.createTextNode(labels.filter_geo_trigger || `[label missing: location.filter_geo_trigger]`)
        );
        this.#elLabelInfo?.replaceChildren(
          document.createTextNode(labels.filter_geo_hint || `[label missing: location.filter_geo_hint]`)
        );
        if (this.#elGeoToggle) this.#elGeoToggle.disabled = false;
        break;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
   * PANEL CLOSE — Notification for postal source
   * ───────────────────────────────────────────────────────────────────── */

  #setupPanelClose() {
    if (!this.#panelId) return;

    this.addDisposable(
      on(LISTEN.PANEL_CLOSED, (e) => {
        if (e.detail?.id !== this.#panelId) return;
        this.#handlePanelClosed();
      })
    );
  }

  /**
   * Fires notification when panel closes with a resolved postal code.
   * Geo notifications are handled inline (immediate feedback in #handleGeoClick).
   * Postal notifications only fire here — after user confirms with "Speichern".
   */
  #handlePanelClosed() {
    if (!this.#resolvedCode || this.#resolvedSource !== 'postal') return;
    this.#notify('success', 'notification_postal_found', this.#resolvedCode);
  }

  #setupPermissionWatch() {
    if (!navigator.permissions?.query) return;

    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      const handler = () => {
        if (
          (status.state === 'prompt' || status.state === 'granted') &&
          this.state.has(STATES.DENIED)
        ) {
          this.state.remove(STATES.DENIED);
          this.#updateGeoButton(null);
        }
      };
      status.addEventListener('change', handler);
      this.addDisposable(() => status.removeEventListener('change', handler));
    }).catch(() => { /* silent */ });
  }

  /* ─────────────────────────────────────────────────────────────────────
   * POSTAL INPUT
   * ───────────────────────────────────────────────────────────────────── */

  #setupPostal() {
    if (!this.#elPostalInput) return;

    const debouncedResolve = debounce(() => this.#resolvePostalInput(), 140);
    const handler = (ev) => {
      ev?.type === 'input' ? debouncedResolve() : this.#resolvePostalInput();
    };

    for (const evName of this.#declaredEvents) {
      this.#elPostalInput.addEventListener(evName, handler, { passive: true });
      this.addDisposable(() => this.#elPostalInput.removeEventListener(evName, handler));
    }

    if (this.#elPostalDelete) {
      const handleDelete = () => this.#handleDelete();
      this.#elPostalDelete.addEventListener('click', handleDelete, { passive: true });
      this.addDisposable(() => this.#elPostalDelete.removeEventListener('click', handleDelete));
    }

    const handlePageShow = (e) => {
      if (e?.persisted) this.#resolvePostalInput();
    };
    window.addEventListener('pageshow', handlePageShow);
    this.addDisposable(() => window.removeEventListener('pageshow', handlePageShow));
  }

  /**
   * Resolve postal input via Zippopotam.us API.
   * Async with version guard to prevent stale results from overwriting.
   */
  async #resolvePostalInput() {
    // Clear previous state when user edits PLZ manually
    if (GEO_STATE_TOKENS.some(t => this.state.has(t))) {
      this.state.remove(...GEO_STATE_TOKENS);
      this.#updateGeoButton(null);
      this.#updateCountryDisplay('');
    }

    const raw = this.#getInputValue();
    const code = geo.extractPostal(raw);

    // Truly empty — user cleared the field
    if (!code && !raw) {
      this.#resolveVersion++;
      this.#resolvedCode = null;
      this.#resolvedCity = null;
      this.#resolvedSource = null;
      this.#updatePostalDisplay(null);
      this.#updateCityDisplay(null);
      deleteLocation();
      this.emit(EVENTS.RESOLVED, { valid: false, reason: 'cleared' });
      return;
    }

    // Non-empty but no valid PLZ — invalid format
    if (!code) {
      this.#resolveVersion++;
      this.state.add(STATES.ERROR);
      this.emit(EVENTS.RESOLVED, { valid: false, reason: 'invalid', raw });
      return;
    }

    // Pattern validation (instant, before API call)
    if (this.#patternRe && !this.#patternRe.test(code)) {
      this.#resolveVersion++;
      this.state.add(STATES.ERROR);
      this.emit(EVENTS.RESOLVED, { valid: false, reason: 'unknown', code });
      return;
    }

    // Async API call with version guard
    const version = ++this.#resolveVersion;

    const result = await geo.resolvePostalAPI(code);

    if (version !== this.#resolveVersion) return;

    if (!result) {
      this.state.add(STATES.ERROR);
      this.emit(EVENTS.RESOLVED, { valid: false, reason: 'unknown', code });
      return;
    }

    this.#setGeoState(STATES.SUCCESS);
    this.#updateGeoButton(STATES.SUCCESS, { city: result.city, state: result.state, countryCode: 'DE' });
    this.#resolvedCode = code;
    this.#resolvedCity = result.city;
    this.#resolvedSource = 'postal';
    this.#updatePostalDisplay(code);
    this.#updateCityDisplay(result.city);
    saveLocation(code, result.city, result.lat, result.lng);

    this.emit(EVENTS.RESOLVED, {
      valid: true,
      lat: result.lat,
      lng: result.lng,
      code,
      city: result.city,
      source: 'postal'
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
   * DELETE + RESTORE
   * ───────────────────────────────────────────────────────────────────── */

  #handleDelete() {
    this.#resolveVersion++;
    deleteLocation();

    if (this.#elPostalInput) {
      this.#elPostalInput.value = '';
    }

    this.state.remove(...GEO_STATE_TOKENS);
    this.#updateGeoButton(null);
    this.#updateCountryDisplay('');

    this.#resolvedCode = null;
    this.#resolvedCity = null;
    this.#resolvedSource = null;
    this.#updatePostalDisplay(null);
    this.#updateCityDisplay(null);

    this.emit(EVENTS.RESOLVED, { valid: false, reason: 'cleared' });
  }

  /**
   * Restore saved location from localStorage on mount.
   * If full data (lat/lng/city) is cached, emit directly without API call.
   * Otherwise fall back to async resolve.
   */
  #restoreFromStorage() {
    if (!this.#elPostalInput) return;

    const saved = loadLocation();
    if (!saved?.code) return;

    if (!this.#getInputValue()) {
      this.#elPostalInput.value = saved.code;
    }

    // Full cached data — emit directly (no API call)
    if (saved.lat != null && saved.lng != null) {
      this.#resolvedCode = saved.code;
      this.#resolvedCity = saved.city;
      this.#resolvedSource = 'postal';
      this.#setGeoState(STATES.SUCCESS);
      this.#updateGeoButton(STATES.SUCCESS, { city: saved.city });
      this.#updatePostalDisplay(saved.code);
      this.#updateCityDisplay(saved.city);

      this.emit(EVENTS.RESOLVED, {
        valid: true,
        lat: saved.lat,
        lng: saved.lng,
        code: saved.code,
        city: saved.city,
        source: 'postal'
      });
      return;
    }

    // Legacy cache (code only) — resolve via API
    this.#resolvePostalInput();
  }

  /* ─────────────────────────────────────────────────────────────────────
   * HELPERS
   * ───────────────────────────────────────────────────────────────────── */

  #getInputValue() {
    if (!this.#elPostalInput) return '';
    const v = typeof this.#elPostalInput.value === 'string'
      ? this.#elPostalInput.value
      : (this.#elPostalInput.value != null ? String(this.#elPostalInput.value) : '');
    return v.trim();
  }

  #setGeoState(state) {
    this.state.remove(...GEO_STATE_TOKENS);
    this.state.add(state);
  }

  #updatePostalDisplay(code) {
    for (const el of this.#elsPostalDisplay) {
      el.textContent = code || this.#postalDisplayDefault;
    }
  }

  #updateCityDisplay(city) {
    for (const el of this.#elsCityDisplay) {
      el.textContent = city || this.#cityDisplayDefault;
    }
  }

  #updateCountryDisplay(country) {
    for (const el of this.#elsCountryDisplay) {
      el.textContent = country || '';
    }
  }

  #notify(type, labelKey, message) {
    const labels = i18n.get('location') || {};
    const title = labels[labelKey] || `[label missing: location.${labelKey}]`;
    emit('liftr:notification:show', {
      type,
      title,
      ...(message ? { message } : {})
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
   * PUBLIC API
   * ───────────────────────────────────────────────────────────────────── */

  get resolvedCode() {
    return this.#resolvedCode;
  }

  get resolvedCity() {
    return this.#resolvedCity;
  }

  get resolvedSource() {
    return this.#resolvedSource;
  }

  get timezone() {
    return settings.market?.timezone || 'Europe/Berlin';
  }
}

customElements.define('liftr-location', LiftrLocation);

export { LiftrLocation, ROLES, GLOBAL_ROLES, STATES, EVENTS, SETTINGS };